import { gql } from '@apollo/client';

import { IntegrationConnectionStatus, useGetOrgRequiredSetupStatusQuery } from 'src/generated/mloop-graphql';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GetOrgRequiredOnboardingStatus = gql`
  query GetOrgRequiredSetupStatus {
    thisOrg {
      id
      atsStatus
      directoryStatus
    }
  }
`;

/**
 * Maintain not connected logic at single place
 */
const getIsOrgSetupRequiredNotConnected = (status: {
  atsStatus?: IntegrationConnectionStatus;
  directoryStatus?: IntegrationConnectionStatus;
}): boolean => {
  return (
    status.atsStatus === IntegrationConnectionStatus.NotConnected ||
    status.directoryStatus === IntegrationConnectionStatus.NotConnected
  );
};

/**
 * Hook for getIsOrgSetupRequiredNotConnected
 */
export const useIsOrgSetupRequiredNotConnected = (): { notConnected: boolean; loading: boolean } => {
  const { data, loading } = useGetOrgRequiredSetupStatusQuery();
  return {
    notConnected: getIsOrgSetupRequiredNotConnected({
      atsStatus: data?.thisOrg?.atsStatus,
      directoryStatus: data?.thisOrg?.directoryStatus,
    }),
    loading,
  };
};

/**
 * Maintain pending connection logic at single place
 */
const getIsOrgSetupRequiredPending = (status: {
  atsStatus?: IntegrationConnectionStatus;
  directoryStatus?: IntegrationConnectionStatus;
}): boolean => {
  return (
    status.atsStatus === IntegrationConnectionStatus.Syncing ||
    status.directoryStatus === IntegrationConnectionStatus.Syncing
  );
};

/**
 * Hook for getIsOrgSetupRequiredPending
 */
export const useIsOrgSetupRequiredPending = (): { pending: boolean; loading: boolean } => {
  const { data, loading } = useGetOrgRequiredSetupStatusQuery();
  return {
    pending: getIsOrgSetupRequiredPending({
      atsStatus: data?.thisOrg?.atsStatus,
      directoryStatus: data?.thisOrg?.directoryStatus,
    }),
    loading,
  };
};

/**
 * Maintain completed connection logic at single place
 */
const getIsOrgSetupRequiredCompleted = (status: {
  atsStatus?: IntegrationConnectionStatus;
  directoryStatus?: IntegrationConnectionStatus;
}): boolean => {
  return !getIsOrgSetupRequiredNotConnected(status) && !getIsOrgSetupRequiredPending(status);
};

/**
 * Hook for getIsOrgSetupRequiredCompleted
 * Maintain completed connection logic at single place
 */
export const useIsOrgSetupRequiredCompleted = (): { completed: boolean; loading: boolean } => {
  const { data, loading } = useGetOrgRequiredSetupStatusQuery();
  return {
    completed: getIsOrgSetupRequiredCompleted({
      atsStatus: data?.thisOrg?.atsStatus,
      directoryStatus: data?.thisOrg?.directoryStatus,
    }),
    loading,
  };
};
